import { useState, useEffect, useRef } from "react";
import { bucketUrlPrefix } from "common/config";
import ScrollContainer from "react-indiana-drag-scroll";
import { toSentenceCase } from "@/helpers/stringUtils";
import { CaretDownIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import ImageListViewer from "@/components/ImageListViewer/ImageListViewer";
import { VenueReview } from "common/types";
import AnalyticsService from "@/services/analytics";
import { StandardButton } from "@/components/common/StandardButton";
import FullImageViewer from "@/components/FullImageViewer";
import CoolEmoji from "@/assets/images/cool_emoji.svg";
import WeirdEmoji from "@/assets/images/weird_emoji.svg";
import { createPortal } from "react-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardFooter,
  CardContent,
  CardDescription,
} from "@/shadcn/components/ui/card";

function BusinessReview({
  review,
  onFlag,
}: {
  review: VenueReview;
  onFlag: (review: VenueReview) => void;
}) {
  const [showFullText, setShowFullText] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const [showImageList, setShowImageList] = useState<{
    show: boolean;
    current: number;
  }>({
    show: false,
    current: 0,
  });

  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalRoot(document.body), []);

  const toggleShowFullText = () => {
    setShowFullText((prev) => !prev);
  };

  useEffect(() => {
    if (textRef.current) {
      // CheckIcon if the content is overflowing by comparing scroll height and client height
      setIsTruncated(
        textRef.current.scrollHeight > textRef.current.clientHeight,
      );
    }
  }, [review.notes]);

  const handleOpenFlagTracking = () => {
    AnalyticsService.trackGAEventHandler("flagReviewReportOpen");
    AnalyticsService.trackMx("flagReviewReportOpen", {});
  };

  const handleOpenFullReviewImage = (photo) => {
    setCurrentReviewImage(photo);
    setFullReviewImageView(true);

    // Track opening the full image viewer
    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
    AnalyticsService.trackMx("openPhotoLightbox", {
      VenueId: review.businessId,
      VenueBrandId: "Review venue brand id",
      VenueBrandName: "Review venue brand name",
      VenueAddress: "Review venue address",
      VenueName: "Review venue name",
      VenueTypes: "Review venue types",
      CategoryName: "Review photo",
      SubcategoryName: "Review photo",
    });
  };

  const [fullReviewImageView, setFullReviewImageView] = useState(false);
  const [currentReviewImage, setCurrentReviewImage] = useState<any>(null);

  return (
    <Card className="mb-2">
      <CardHeader>
        <CardTitle>
          <div className="w-full min-h-11 py-2 flex justify-between items-center gap-3">
            {/* Left: Profile picture and name/time */}
            <div className="flex justify-start items-center gap-3">
              {/* Left column: Profile picture or name initials */}
              <div className="flex-shrink-0">
                {review.userProfilePhoto ? (
                  <img
                    className="w-9 h-9 rounded-full shadow-md object-cover"
                    src={bucketUrlPrefix + review.userProfilePhoto}
                    alt={review.userFirstName}
                  />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-medium select-none"
                    style={{ backgroundColor: review?.userProfileColorCode ?? "#8751FF", fontSize: "20px" }}
                  >
                    {review.user?.name?.charAt(0)?.toUpperCase() ?? ""}
                  </div>
                )}
              </div>

              {/* Right column: Name and time stacked */}
              <div className="flex flex-col justify-center gap-1">
                <div className="text-fg-neutral-strong label-l-bold">
                  {review.userFirstName}
                </div>
                <span className="label-s-medium text-fg-neutral-subtle">
                  {review.timeAgo}
                </span>
              </div>
            </div>

            {/* Right: Flag button */}
            <div className="flex justify-end items-center flex-shrink-0">
              <StandardButton
                onClick={() => {
                  handleOpenFlagTracking();
                  onFlag(review);
                }}
                variant="icon"
                size="icon"
                className="w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-lg hover:bg-bg-brand-ghost-hover transition-colors"
                aria-label="Report inappropriate review"
              >
                <img src="/images/Flag.svg" alt="" width={20} height={20} aria-hidden="true" />
              </StandardButton>
            </div>
          </div>
        </CardTitle>
        <CardDescription>
        <div className="flex flex-col justify-center gap-1">
            {review.userDisabilityVisible == "1" &&
              review.userAbility.length > 0 && (
                <div className="w-full flex flex-wrap justify-start items-center gap-2">
                  {review.userAbility
                    .split(",")
                    .filter((ability) => ability.trim().toLowerCase() !== "none")
                    .map((ability, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center justify-center h-[40px] px-4
                        rounded-full 
                        bg-bg-brand-secondary-default 
                        border border-border-token-brand-minimal 
                        label-m-medium 
                        text-fg-neutral-strong
                        hover:bg-bg-brand-secondary-hover
                        hover:border-border-token-brand-minimal-hover
                        hover:text-fg-neutral-strong-hover
                        "
                      >
                        {ability.trim()}
                      </span>
                    ))}
                </div>
              )}
          </div>
        </CardDescription>
        
      </CardHeader>

      <CardContent>
        
      <h2 className="sr-only">Review by {review.userFirstName}</h2>
          <div className="w-full flex justify-between items-start gap-4">
            <div className="flex-1 flex flex-col justify-start items-start">
             

              {review.accessibility_experience && (
                <div className="w-full py-2 flex justify-start items-center gap-2">
                  <img
                    src={
                      review.accessibility_experience === "cool"
                        ? CoolEmoji
                        : WeirdEmoji
                    }
                    alt=""
                    className="w-6 h-6"
                    aria-hidden="true"
                  />
                  <span className="label-m-semibold text-fg-neutral-strong">
                    "{review.accessibility_experience === "cool" ? "Cool" : "Weird"}"
                    with my needs
                  </span>
                </div>
              )}
            </div>
            
          </div>
        </CardContent>

        <div className="px-1">
          <p
            ref={textRef}
            className={`label-m-medium text-fg-neutral-subtle ${showFullText ? "" : "line-clamp-3"
              }`}
          >
            {toSentenceCase(review.notes)}
          </p>

          {isTruncated && (
            <StandardButton
              onClick={toggleShowFullText}
              variant="link"
              className="!text-fg-brand-subtle label-m-semibold mt-2 hover:underline flex flex-row justify-center items-center"
              aria-label={showFullText ? "Read less of this review" : "Read more of this review"}
            >
              {showFullText ? "Read Less" : "Read More"}
              <CaretDownIcon
                size={20}
                className={cn("ml-2", showFullText ? "rotate-180" : "")}
                weight="bold"
                aria-hidden="true"
              />
            </StandardButton>
          )}
        </div>

      {review.reviewPhotos.length > 0 && (
        <div className="px-1 py-2">
          <ScrollContainer className="flex flex-row space-x-2">
            {review.reviewPhotos.map((photo, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  handleOpenFullReviewImage(photo);
                }}
                className="cursor-pointer flex-shrink-0 focus-blue rounded-lg"
                aria-label={photo.altText ? `View photo: ${photo.altText}` : `View review photo ${index + 1}`}
              >
                <img
                  src={bucketUrlPrefix + photo.path}
                  className="w-24 h-24 rounded-lg object-cover"
                  alt={photo.altText || "Review photo"}
                />
              </button>
            ))}
          </ScrollContainer>

          {/* Full image view */}
          {fullReviewImageView &&
            currentReviewImage &&
            portalRoot &&
            createPortal(
              <div className="fixed inset-0 z-[999] bg-black bg-opacity-90">
                <FullImageViewer
                  images={review.reviewPhotos.map((photo, index) => ({
                    src: bucketUrlPrefix + photo.path,
                    alt: photo.altText || "Review photo" + (index + 1),
                    tag_name:
                      review.accessibility_experience === "cool"
                        ? "Sweet experience"
                        : "Sour experience",
                    category_name: photo.tagApplied,
                    created_at: null,
                    photo_type: "Review type",
                    review_photos_id: null,
                    width: photo.width,
                    height: photo.height,
                    sort_order: photo.sort_order,
                    qtags: [],
                    tags: [
                      {
                        tagName: "Review Photo",
                        categoryDisplaySectionId: "",
                      },
                    ],
                    displaySections: {},
                    venueId: review.businessId,
                    venueName: "Review venue name",
                    venueAddress: "Review venue address",
                    venueBrandId: "Review venue brand id",
                    venueBrandName: "Review venue brand name",
                    venueTypes: "Review venue types",
                    user_roles: { isGeneralUser: true },
                    isExperiencePhoto: true,
                    review_user_first_name: review.userFirstName,
                    review_user_last_name: review.userLastName,
                    review_user_profile_photo: review.userProfilePhoto
                      ? bucketUrlPrefix + review.userProfilePhoto
                      : undefined,
                    review_user_color: review.userProfileColorCode,
                    review_notes: review.notes,
                    review_time_ago: review.timeAgo,
                    review_accessibility_experience: review.accessibility_experience,
                  }))}
                  currentIndex={
                    review.reviewPhotos.findIndex((p) => p === currentReviewImage) || 0
                  }
                  onClose={() => setFullReviewImageView(false)}
                  onFlag={() => {}}
                  showFlag={false}
                  showRightTab={true}
                />
              </div>,
              portalRoot,
            )}
        </div>
      )}

      <ImageListViewer
        title="Photos of Reviews"
        currentImage={showImageList.current}
        images={review.reviewPhotos.map((photo) => ({
          id: photo.path,
          url: bucketUrlPrefix + photo.path,
          username: review.user.name,
          timeAgo: review.timeAgo,
          note: photo.altText,
        }))}
        visible={showImageList.show}
        onClose={() => {
          setShowImageList({
            show: false,
            current: 0,
          });
        }}
      />
    </Card>
  );
}

export default BusinessReview;
